import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  CloudToBridgeCommand,
  type IBaseCommand,
  type IDeviceCommand,
} from '@principle-theorem/principle-bridge-core';
import { BridgeDevice, Practice } from '@principle-theorem/principle-core';
import {
  type IBridgeDevice,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type IAttachment,
  type WithRef,
  all$,
  getParentDocRef,
  serialise,
} from '@principle-theorem/shared';
import { AttachmentsComponent } from '@principle-theorem/ng-shared';
import { AsyncPipe } from '@angular/common';
import { push, set } from 'firebase/database';
import { type Observable } from 'rxjs';

export interface IViewBridgeLogsDialogData {
  device: WithRef<IBridgeDevice>;
  practice: WithRef<IPractice>;
}

@Component({
  selector: 'pt-view-bridge-logs-dialog',
  templateUrl: './view-bridge-logs-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    AttachmentsComponent,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
})
export class ViewBridgeLogsDialogComponent {
  private _data = inject<IViewBridgeLogsDialogData>(MAT_DIALOG_DATA);

  logs$: Observable<IAttachment[]>;
  requesting = signal(false);
  successMessage = signal<string | undefined>(undefined);
  errorMessage = signal<string | undefined>(undefined);

  constructor() {
    this.logs$ = all$(BridgeDevice.fileCol(this._data.device));
  }

  async requestLogs(): Promise<void> {
    const { device, practice } = this._data;
    if (!device.deviceId) {
      return;
    }

    this.requesting.set(true);
    this.successMessage.set(undefined);
    this.errorMessage.set(undefined);

    try {
      const brandRef = Practice.brandDoc(practice);
      const organisationId = getParentDocRef(brandRef).id;

      const command: IBaseCommand<CloudToBridgeCommand.GetLogs, object> = {
        type: CloudToBridgeCommand.GetLogs,
        data: {},
      };

      const deviceCommand: IDeviceCommand = {
        deviceRef: device.ref,
        practiceRef: practice.ref,
        brandRef,
        command,
      };

      const deviceDatabaseRef = BridgeDevice.getDeviceDatabaseRef(
        organisationId,
        device.deviceId,
        '/commands'
      );
      const commandRef = await push(deviceDatabaseRef);
      await set(commandRef, serialise(deviceCommand));

      this.successMessage.set('Logs requested successfully');
    } catch {
      this.errorMessage.set('Failed to request logs');
    } finally {
      this.requesting.set(false);
    }
  }
}
